import * as React from "react"
import { StaticImage } from "gatsby-plugin-image"
import styled from "@emotion/styled"
import { keyframes } from "@emotion/react"
import { AiOutlineInstagram } from "@react-icons/all-files/ai/AiOutlineInstagram"
import { AiOutlineLinkedin } from "@react-icons/all-files/ai/AiOutlineLinkedin"
import { AiOutlineGithub } from "@react-icons/all-files/ai/AiOutlineGithub"

import Layout from "../components/layout"
import Seo from "../components/seo"

// --- Animations ---
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`

const pulse = keyframes`
  0% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.1); }
  100% { opacity: 0.4; transform: scale(1); }
`

// --- Layout Components ---

const Wrapper = styled.div`
  position: relative;
  min-height: 100vh;
  background-color: #111111;
  color: #e2e2e2;
  overflow: hidden; /* Prevent horizontal scroll from visual elements */
  display: flex;
  flex-direction: column;
`

// Dynamic Spotlight Background (Breathing Orbs)
const SpotlightBg = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  pointer-events: none;
`

const Orb1 = styled.div`
  position: absolute;
  top: -20%;
  right: -10%;
  width: 60vw;
  height: 60vw;
  background: radial-gradient(
    circle,
    rgba(220, 140, 80, 0.35) 0%,
    transparent 60%
  );
  filter: blur(80px);
  animation: ${pulse} 15s infinite ease-in-out;
`

const Orb2 = styled.div`
  position: absolute;
  bottom: -10%;
  left: -10%;
  width: 50vw;
  height: 50vw;
  background: radial-gradient(
    circle,
    rgba(100, 120, 80, 0.3) 0%,
    transparent 50%
  );
  filter: blur(80px);
  animation: ${pulse} 20s infinite ease-in-out reverse;
`

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  max-width: 1400px;
  margin: 0 auto;
  padding: 1.5rem;
  width: 100%;
  flex: 1;
  align-items: center;
  z-index: 1;
  position: relative;

  @media (min-width: 768px) {
    padding: 2rem;
  }

  @media (min-width: 1024px) {
    grid-template-columns: 1.2fr 0.8fr;
    gap: 6rem;
    padding: 4rem 6rem;
    min-height: 100vh; /* Ensure full height content area */
  }
`

// --- Typography & Content ---

const TextSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  animation: ${fadeIn} 1.2s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;

  @media (max-width: 1023px) {
    text-align: center;
    margin-top: 0;
    align-items: center;
  }
`

const RoleLabel = styled.h2`
  font-family: "SF Mono", "Fira Code", monospace;
  font-size: 0.85rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: #998877;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (min-width: 768px) {
    font-size: 1rem;
  }

  &:before {
    content: "";
    display: block;
    width: 30px;
    height: 1px;
    background: rgba(220, 140, 80, 0.3);

    @media (max-width: 1023px) {
      display: none;
    }
  }
`

const NameHeadline = styled.h1`
  font-family: -apple-system, BlinkMacSystemFont, "Inter", "Segoe UI",
    sans-serif;
  font-size: 2.5rem;
  line-height: 1.1;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: #f5f0e8;
  margin-bottom: 1.5rem;

  @media (min-width: 480px) {
    font-size: 3rem;
  }

  @media (min-width: 768px) {
    font-size: 4rem;
    margin-bottom: 2rem;
  }

  @media (min-width: 1280px) {
    font-size: 5rem;
  }
`

const BioText = styled.div`
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
  font-size: 0.95rem;
  line-height: 1.6;
  color: #c5b8a8;
  max-width: 500px;
  font-weight: 400;
  margin-bottom: 2rem;

  p {
    margin-bottom: 1rem;
  }

  a {
    color: #e8dcc8;
    text-decoration: none;
    border-bottom: 1px solid rgba(220, 140, 80, 0.3);
    transition: all 0.2s ease;

    &:hover {
      border-bottom-color: rgba(220, 140, 80, 0.8);
      color: rgba(220, 140, 80, 0.9);
    }
  }

  @media (min-width: 768px) {
    font-size: 1.05rem;
    margin-bottom: 3rem;

    p {
      margin-bottom: 1.25rem;
    }
  }

  @media (max-width: 1023px) {
    margin-left: auto;
    margin-right: auto;
  }
`

const SocialRow = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-top: 0.5rem;

  @media (min-width: 768px) {
    gap: 2rem;
  }

  a {
    color: #888888;
    font-size: 1.5rem;
    transition: transform 0.2s ease, color 0.2s ease;

    &:hover {
      color: rgba(220, 140, 80, 0.9);
      transform: translateY(-2px);
    }
  }
`

// --- Visual Section ---

const VisualSection = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: ${fadeIn} 1.4s cubic-bezier(0.2, 0.8, 0.2, 1) forwards 0.2s;
  opacity: 0;

  @media (max-width: 1023px) {
    height: 300px;
    margin-bottom: 1rem;
    order: -1; /* Move image to top on mobile */
  }

  @media (min-width: 480px) and (max-width: 1023px) {
    height: 350px;
    margin-bottom: 1.5rem;
  }
`

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 420px;
  aspect-ratio: 1;

  &:before {
    content: "";
    position: absolute;
    inset: -20px;
    background: radial-gradient(
      ellipse at 50% 50%,
      rgba(220, 140, 80, 0.08) 0%,
      transparent 70%
    );
    z-index: -1;
    border-radius: 8px;
  }
`

// Styled to look like a high-end magazine cutout or art piece
const StyledImage = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4),
              0 0 40px rgba(220, 140, 80, 0.1);

  .gatsby-image-wrapper {
    height: 100%;
    width: 100%;
  }
`

const DecorationLine = styled.div`
  position: absolute;
  bottom: -2rem;
  right: -2rem;
  width: 150px;
  height: 150px;
  border-right: 1px solid rgba(220, 140, 80, 0.2);
  border-bottom: 1px solid rgba(220, 140, 80, 0.2);
  z-index: -1;
`

const IndexPage = ({ location }) => {
  return (
    <Layout location={location}>
      <Seo title="Phillip Michalowski" />
      <Wrapper>
        <SpotlightBg>
          <Orb1 />
          <Orb2 />
        </SpotlightBg>

        <MainGrid>
          <TextSection>
            <RoleLabel>Senior Software Engineer</RoleLabel>
            <NameHeadline>
              PHILLIP
              <br />
              MICHALOWSKI
            </NameHeadline>

            <BioText>
              <p>
                Product engineer building full-stack web applications with
                Next.js, TypeScript, and Ruby on Rails.
              </p>
              <p>
                Currently at{" "}
                <a
                  href="https://www.ezcater.com/company/about-us/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  ezCater
                </a>
                , working on e-commerce marketplace experiences.
              </p>
              <p>
                Outside of work, I train Brazilian Jiu-Jitsu (purple belt),
                produce music, explore photography, and have a broad interest in
                information acquisition.
              </p>
            </BioText>

            <SocialRow>
              <a
                href="https://github.com/onocy"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub"
              >
                <AiOutlineGithub />
              </a>
              <a
                href="https://www.linkedin.com/in/phillip-michalowski/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <AiOutlineLinkedin />
              </a>
              <a
                href="https://www.instagram.com/onocy/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <AiOutlineInstagram />
              </a>
            </SocialRow>
          </TextSection>

          <VisualSection>
            <ImageContainer>
              <StyledImage>
                <StaticImage
                  layout="fullWidth"
                  formats={["AUTO", "WEBP", "AVIF"]}
                  src="../images/new_headshot.JPG"
                  quality={100}
                  alt="Portrait"
                  style={{ height: "100%" }}
                  imgStyle={{ objectFit: "cover" }}
                />
              </StyledImage>
              <DecorationLine />
            </ImageContainer>
          </VisualSection>
        </MainGrid>
      </Wrapper>
    </Layout>
  )
}

export default IndexPage
